import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../icons/icon';

/**
 * Sheet no mobile que vira dialog a partir de 768 px — o MESMO componente (canvas T2.1, `overlay()`).
 *
 * Contrato C8: foco entra no primeiro controlo útil, fica contido enquanto aberto (Tab e Shift+Tab
 * dão a volta) e regressa ao elemento que abriu ao fechar. Esc e o toque no fundo fecham. O fundo
 * não rola por baixo — sem isso, o gesto de fechar a sheet no iOS arrastava a página.
 */
export interface OverlayDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Parágrafo que descreve o diálogo (`aria-describedby`), quando existe. */
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Elemento que recebe o foco inicial; por omissão, o primeiro focável do corpo. */
  initialFocus?: () => HTMLElement | null;
  testId?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function OverlayDialog({ open, onClose, title, description, children, footer, initialFocus, testId }: OverlayDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const panel = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const initialFocusRef = useRef(initialFocus);
  initialFocusRef.current = initialFocus;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const target =
      initialFocusRef.current?.() ??
      bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panel.current;
    target?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.closest('[hidden]'));
      if (items.length === 0) {
        event.preventDefault();
        panel.current.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    // Toque no fundo fecha. Ouvinte nativo: o fundo é decorativo (sem papel nem foco), e o teclado
    // já tem o Esc e o botão Fechar — um handler JSX aqui seria interação sem equivalente focável.
    const scrimEl = scrim.current;
    const onScrim = () => onCloseRef.current();
    scrimEl?.addEventListener('click', onScrim);

    return () => {
      document.removeEventListener('keydown', onKey);
      scrimEl?.removeEventListener('click', onScrim);
      document.body.style.overflow = previousOverflow;
      // O opener pode ter saído do DOM (ex.: a lista recarregou); aí o foco cai no <body>, que é
      // o comportamento nativo de um <dialog> — melhor do que focar um nó desligado.
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="pc-overlay" data-testid={testId}>
      <div ref={scrim} className="pc-scrim" aria-hidden="true" data-testid="overlay-scrim" />
      <section
        ref={panel}
        className="sheet pc-ov"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
      >
        <div className="grip" aria-hidden="true" />
        <div className="pc-ov-head">
          <h2 className="pc-h2" id={titleId}>{title}</h2>
          <button className="hit pc-iconbtn" type="button" aria-label={t('actions.close')} onClick={() => onCloseRef.current()}>
            <Icon name="close" />
          </button>
        </div>
        <div ref={bodyRef} className="pc-ov-body">
          {description ? <p className="pc-body" id={descId}>{description}</p> : null}
          {children}
        </div>
        {footer ? <div className="pc-ov-foot">{footer}</div> : null}
      </section>
    </div>,
    document.body,
  );
}
